// Which mailing(s) can an event be advertised in? (DCP-2228 follow-up)
//
// Two rules, both purely temporal:
//   1. Period 1: the event's start date falls WITHIN period 1 of some mailing,
//      both bounds inclusive, and that mailing's cutoff has not passed. Always
//      checked, for every event.
//   2. Period 2: ONLY when the event's type has `hasExtraPeriod` — some
//      categories (e.g. Degu-Menüs & Hoffeste) need a longer lead time, so
//      they are additionally advertised via a mailing's SECOND period. Same
//      inclusive-bounds-plus-open-cutoff rule, just against period 2.
//
// The two matches are usually two DIFFERENT mailing records: a mailing's
// period 2 is typically the same calendar window as a later mailing's
// period 1 (that is the whole point of the "extra period" — announce early).
// If both rules happen to land on the same mailing, it is reported once, not
// twice.
//
// Mirrors the backend's findAvailableMailings() (used once, at creation time,
// to compute DepotEvent.availableMailings) — this is the LIVE version shown
// while a VA is being created or edited, so it has to react to the date field
// and the selected VA type as they change.
//
// This runs client-side on purpose: a server round trip per keystroke on the
// date field would be both slow and pointless, since the rule needs no data
// beyond the mailing list itself.
//
// Comparison is on whole days. `startAt` is an ISO *datetime* while the mailing
// dates are plain days, so comparing them as Date objects would put an event at
// 00:30 local time on the wrong side of a boundary.

import type { Mailing } from '@/types';

export type MatchedPeriod = 'period1' | 'period2';

export interface MailingMatch {
  mailing: Mailing;
  /** Which of the mailing's two periods produced this match. */
  matchedPeriod: MatchedPeriod;
}

/** The day part of an ISO date or datetime. */
function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * German day format (14.08.2026) from an ISO day.
 *
 * Lives here next to the other mailing day handling because both the event panel
 * and the maintenance list render these dates — a third private copy is how two of
 * them end up formatting differently.
 */
export function formatDay(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day && month && year ? `${day}.${month}.${year}` : iso;
}

/**
 * Returns the mailing(s) the event is eligible for, period 1 first.
 *
 * `startAt` may be empty — the create dialog calls this before a date is picked.
 * Without a date no mailing can be judged, so the result is empty rather than
 * "the first one" or "all of them".
 *
 * `today` is a parameter rather than read from the clock so the boundary cases are
 * testable and one render is internally consistent.
 */
export function findMatchingMailings(
  mailings: readonly Mailing[],
  startAt: string | undefined,
  today: Date,
  hasExtraPeriod: boolean
): MailingMatch[] {
  if (!startAt) return [];

  const eventDay = dayOf(startAt);
  const todayDay = today.toISOString().slice(0, 10);

  const matches: MailingMatch[] = [];

  const period1Match = mailings.find(
    (mailing) =>
      dayOf(mailing.period1From) <= eventDay &&
      eventDay <= dayOf(mailing.period1To) &&
      dayOf(mailing.cutoffDate) >= todayDay
  );
  if (period1Match) matches.push({ mailing: period1Match, matchedPeriod: 'period1' });

  if (hasExtraPeriod) {
    const period2Match = mailings.find(
      (mailing) =>
        mailing.period2From !== undefined &&
        mailing.period2To !== undefined &&
        dayOf(mailing.period2From) <= eventDay &&
        eventDay <= dayOf(mailing.period2To) &&
        dayOf(mailing.cutoffDate) >= todayDay
    );
    if (period2Match && !matches.some((m) => m.mailing.key === period2Match.key)) {
      matches.push({ mailing: period2Match, matchedPeriod: 'period2' });
    }
  }

  return matches;
}
